/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { injectable } from 'inversify';
import { ViewPortController } from '@/components/viewPort/viewPortController';
import type { IViewPortControllerFactory, IViewPortController, ITwoDLayerController } from './interfaces';
import * as THREE from 'three';
import { iocContainer, TYPES } from '@/engine/ioc';

@injectable()
export class ViewPortControllerFactory implements IViewPortControllerFactory {
  private readonly _renderersCache = new Map<string, THREE.WebGLRenderer>();

  private _createRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true // [chebotarev, namazov]: need this to be able to get screenshots from canvas
    });
    renderer.setTransparentSort((a, b): number => {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions -- TODO: [chebotarev] fix eslint strict-boolean-expressions
      if (a.material.depthWrite && b.material.depthWrite && a.material.opacity !== b.material.opacity) {
        return b.material.opacity - a.material.opacity;
      } else {
        // reversePainterSortStable function @ src/renderers/webgl/WebGLRenderLists.js
        // link(r168): https://github.com/mrdoob/three.js/blob/17abdb5e4b88368a6c1528743d014e641b32cf85/src/renderers/webgl/WebGLRenderLists.js#L27
        if (a.groupOrder !== b.groupOrder) {
          return a.groupOrder - b.groupOrder;
        } else if (a.renderOrder !== b.renderOrder) {
          return a.renderOrder - b.renderOrder;
        } else if (a.z !== b.z) {
          return b.z - a.z;
        } else {
          return a.id - b.id;
        }
      }
    });
    return renderer;
  }

  public createViewPortController(
    descriptor: string,
    canvas: HTMLCanvasElement,
    canvasContainer: HTMLDivElement,
    bgColor: number
  ): IViewPortController {
    let renderer = this._renderersCache.get(descriptor);
    if (renderer && renderer.domElement != canvas) {
      renderer.forceContextLoss();
      renderer.dispose();
      renderer = undefined;
    }
    if (!renderer) {
      renderer = this._createRenderer(canvas);
      this._renderersCache.set(descriptor, renderer);
    }

    const twoDLayerController = iocContainer.get<ITwoDLayerController>(TYPES.TwoDLayerController);

    return new ViewPortController(canvas, canvasContainer, renderer, bgColor, twoDLayerController);
  }

  public updateViewPortController(
    vpc: IViewPortController,
    descriptor: string,
    canvas: HTMLCanvasElement,
    canvasContainer: HTMLDivElement
  ): void {
    let renderer = this._renderersCache.get(descriptor);
    if (renderer && renderer.domElement != canvas) {
      renderer.forceContextLoss();
      renderer.dispose();
      renderer = undefined;
    }
    if (!renderer) {
      renderer = this._createRenderer(canvas);
      this._renderersCache.set(descriptor, renderer);
      vpc.reInitialize(canvas, canvasContainer, renderer);
    }
  }
}
